import { byPassSpecialCharactersInLikeOperator } from '@testsigma/organism';
import { handleSessionExpired } from '@utils/api-handler-utils';
import axios from 'axios';
import { isValidJson } from '@testsigma/atoms';

export const getBaseUrl = async () => {
  return '';
};

// Add a response interceptor
axios.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response.status === 401) {
      handleSessionExpired();
    }
    return Promise.reject(error);
  },
);

const get = <T>(
  endpoint: string,
  params?: Record<string, any>,
  headers?: Record<string, string | number | boolean>,
  paramsSerializer?: (params: Record<string, any>) => string,
): Promise<T> =>
  new Promise(async (resolve, reject) => {
    const baseURL = await getBaseUrl();
    if (params?.query) {
      if (!params?.query?.query && params?.query?.includes(':*')) {
        params.query = byPassSpecialCharactersInLikeOperator(params?.query);
      } else if (params?.query?.query?.includes(':*')) {
        //to handle the params passed by useFetchList
        params.query.query = byPassSpecialCharactersInLikeOperator(
          params?.query?.query,
        );
      }
    }
    return axios
      .get(baseURL + endpoint, {
        params,
        data: {},
        headers: headers || {
          'Content-Type': 'application/json',
        },
        paramsSerializer,
      })
      .then((response) => resolve(response.data))
      .catch((error) => {
        reject(error);
      });
  });

const post = <T>(
  endpoint: string,
  data: object,
  contentType?: string,
  params?: Record<string, any>,
  abortSignal?: AbortSignal,
): Promise<T> =>
  new Promise(async (resolve, reject) => {
    const baseURL = await getBaseUrl();
    const headers = {
      'Content-Type': contentType ? contentType : 'application/json',
    };
    axios(baseURL + endpoint, {
      method: 'POST',
      data: contentType === 'multipart/form-data' ? data : JSON.stringify(data),
      headers,
      params,
      signal: abortSignal,
    })
      .then(async (response) => {
        const content = await response.data;
        return isValidJson(content)
          ? JSON.parse(content === '' ? JSON.stringify({}) : content)
          : content;
      })
      .then((data) => {
        resolve(data);
      })
      .catch((error) => {
        reject(error);
      });
  });

const put = <T>(
  endpoint: string,
  data: object,
  params?: Record<string, any>,
  contentType?: string,
): Promise<T> =>
  new Promise(async (resolve, reject) => {
    const baseURL = await getBaseUrl();
    const headers = {
      'Content-Type': contentType ? contentType : 'application/json',
    };
    axios(baseURL + endpoint, {
      method: 'PUT',
      data: contentType === 'multipart/form-data' ? data : JSON.stringify(data),
      params,
      headers,
    })
      .then(async (response) => {
        const content = await response.data;
        return isValidJson(content)
          ? JSON.parse(content === '' ? JSON.stringify({}) : content)
          : content;
      })
      .then((data: any) => {
        if (data.error) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });

const remove = (
  endpoint: string,
  params?: Record<string, any>,
  headers?: Record<string, string | number | boolean>,
  data?: object,
  abortSignal?: AbortSignal,
): Promise<void> =>
  new Promise(async (resolve, reject) => {
    const baseURL = await getBaseUrl();
    axios
      .delete(baseURL + endpoint, {
        method: 'DELETE',
        params,
        headers,
        data,
        signal: abortSignal,
      })
      .then(() => {
        resolve();
      })
      .catch((error) => {
        reject(error);
      });
  });

const patch = <T>(
  endpoint: string,
  data: string | object,
  params?: Record<string, any>,
  contentType?: string,
): Promise<T> =>
  new Promise(async (resolve, reject) => {
    const baseURL = await getBaseUrl();
    const headers = {
      'Content-Type': contentType ? contentType : 'application/json',
    };
    axios(baseURL + endpoint, {
      method: 'PATCH',
      data:
        contentType === 'multipart/form-data' || typeof data === 'string'
          ? data
          : JSON.stringify(data),
      params,
      headers,
    })
      .then(async (response) => {
        const content = await response.data;
        return isValidJson(content)
          ? JSON.parse(content === '' ? JSON.stringify({}) : content)
          : content;
      })
      .then((data: any) => {
        if (data.error) {
          reject(data);
        } else {
          resolve(data);
        }
      })
      .catch((error) => {
        reject(error);
      });
  });

const Api = {
  get,
  post,
  put,
  remove,
  patch,
};

export default Api;
